import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Feedback } from '../ui/feedback.js';
import { useFieldJob } from '../../lib/field-job.js';
import { enqueueFieldEvent, shouldQueueFieldEvent } from '../../lib/outbox.js';
import { drainOutbox } from '../../lib/outbox-processor.js';
import {
  SignaturePadComponent,
  type CapturedSignature,
} from '../signature-pad.js';

/**
 * Captures the customer signature against the active sales order and pushes
 * it as a CRM-note attachment using the existing salesOrders.attachSignature
 * endpoint. On success, sets the field-job signed flag so Onsite can enable
 * the "Clock out & complete" action.
 */
export function SignScreen() {
  const orderId = useFieldJob((s) => s.currentOrderId);
  const goOnsite = useFieldJob((s) => s.goOnsite);
  const markSigned = useFieldJob((s) => s.markSigned);
  const parts = useFieldJob((s) => s.parts);

  const [captured, setCaptured] = useState<CapturedSignature | null>(null);
  // Printed signer name — becomes part of the CRM-note subject so the
  // signature record answers "who signed" without opening the image
  // (mockup shows "M. Mason · 9:43 AM"; audit flagged the missing identity).
  const [signerName, setSignerName] = useState('');

  const orderQuery = useQuery({
    queryKey: ['sales-orders', 'detail', orderId],
    queryFn: ({ signal }) => api.salesOrders.getById(orderId!, signal),
    enabled: orderId !== null,
    staleTime: 30_000,
  });

  const signatureSubject = () =>
    signerName.trim()
      ? `Tinsmith field signature — ${signerName.trim()}`
      : 'Tinsmith field signature';

  const upload = useMutation({
    mutationFn: async (sig: CapturedSignature) => {
      if (orderId === null) throw new Error('No active order');
      return api.salesOrders.attachSignature(orderId, {
        contentType: 'image/png',
        base64: sig.base64,
        subject: signatureSubject(),
      });
    },
    onSuccess: () => {
      markSigned();
      goOnsite();
    },
    onError: async (err: unknown, sig) => {
      // Network / 5xx — queue the signature bytes so the visit can still
      // be closed out offline. Tech sees the inline error first; we
      // proceed to mark signed + return to Onsite only if we managed to
      // queue. The Clock-out flow is gated on `signed`, so a failed
      // queue + failed sync leaves the tech on Sign screen with a clear
      // error to retry.
      if (shouldQueueFieldEvent(err) && orderId !== null) {
        await enqueueFieldEvent({
          spireSalesOrderId: orderId,
          spireOrderNo: orderQuery.data?.orderNo ?? String(orderId),
          payload: {
            kind: 'attachSignature',
            data: {
              contentType: 'image/png',
              base64: sig.base64,
              subject: signatureSubject(),
            },
          },
        });
        markSigned();
        goOnsite();
        void drainOutbox();
      }
    },
  });

  if (orderId === null) {
    return <div className="text-sm text-ink-muted">No active job.</div>;
  }

  const order = orderQuery.data;
  // The mockup computes parts + labor + tax. We don't have labor tracking
  // yet so we just surface order.total (Spire's authoritative number) and
  // the locally-captured parts. Real review screen needs labor + tax once
  // those are tracked server-side.
  const partsTotal = parts.reduce(
    (sum, p) => sum + Number(p.qty) * Number(p.unitPrice),
    0,
  );

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={goOnsite}
          aria-label="Back to job"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="text-sm font-medium text-ink">Customer signature</div>
        {/* Spacer keeps the title centered; the mockup's Share affordance
            was a dead button and got removed (audit). */}
        <div className="h-9 w-9" aria-hidden />
      </header>

      <div className="rounded-lg border bg-surface p-3 space-y-2" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Review work order</div>
        <div className="text-sm font-medium text-ink">
          {order?.customer.name ?? 'Loading…'} · {order?.orderNo ?? ''}
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-xs">
          <dt className="text-ink-muted">Parts ({parts.length})</dt>
          <dd className="text-right tabular-nums">${partsTotal.toFixed(2)}</dd>
          <dt className="text-ink-muted">Order total (Spire)</dt>
          <dd className="text-right tabular-nums">${order?.total ?? '—'}</dd>
        </dl>
      </div>

      <label className="block space-y-1">
        <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
          Signer name (optional)
        </span>
        <input
          type="text"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="Who's signing? e.g. M. Mason"
          autoComplete="off"
          className="w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        />
      </label>

      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Sign below</div>
      <SignaturePadComponent
        onCapture={(sig) => setCaptured(sig)}
        onClear={() => setCaptured(null)}
      />

      {upload.isError && (
        <Feedback variant="error">
          Couldn&apos;t push signature: {(upload.error as Error | null)?.message ?? 'unknown error'}
        </Feedback>
      )}

      <button
        type="button"
        onClick={() => captured && upload.mutate(captured)}
        disabled={!captured || upload.isPending}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: 'var(--color-live-card-bg-deep)' }}
      >
        <Check className="h-4 w-4" />
        {upload.isPending ? 'Uploading…' : 'Confirm signature'}
      </button>
      <p className="text-center text-[11px] text-ink-muted">
        Attaches as a CRM note on {order?.orderNo ?? 'this order'} in Spire.
      </p>
    </div>
  );
}
